import React, { useEffect, useState } from 'react';
import { usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const COUNTRY_FLAG = { CM: 'CM', CI: 'CI', SN: 'SN', BF: 'BF', GH: 'GH', GN: 'GN', ML: 'ML', TG: 'TG', BJ: 'BJ' };

function Avatar({ name }) {
  const clean = String(name || 'Parent');
  const initials = clean.split(' ').filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase() || 'P';
  const colors = ['#1e4db7', '#a371f7', '#f5a623', '#22c55e', '#e63946'];
  const color = colors[clean.charCodeAt(0) % colors.length];
  return (
    <div style={{ width: 32, height: 32, borderRadius: '50%', background: color + '22', border: `1px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color, fontWeight: 800, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

function formatDate(value) {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'n/a';
  return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function ageText(value) {
  if (!value) return 'n/a';
  const ms = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(ms)) return 'n/a';
  const minutes = Math.max(0, Math.round(ms / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} j`;
}

export default function ClientsPage() {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [parents, setParents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resetLoading, setResetLoading] = useState(null);
  const { user } = useAuth();
  const canResetPassword = Array.isArray(user?.permissions) && user.permissions.includes('support:password_reset');

  async function loadParents(query = appliedSearch) {
    setLoading(true);
    try {
      const { data } = await usersAPI.getParents(1, 50, query);
      if (!data.ok) throw new Error(data.error || 'Lecture DB impossible');
      setParents(data.parents || []);
      setTotal(data.total || (data.parents || []).length);
      setDetail((current) => current ? (data.parents || []).find((p) => p.id === current.id) || null : null);
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Erreur chargement abonnés');
      setParents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadParents(''); }, []);

  const filtered = parents;

  function submitSearch(event) {
    event.preventDefault();
    const q = search.trim();
    setAppliedSearch(q);
    loadParents(q);
  }

  async function resetMobilePassword(parent) {
    const phone = parent?.phone;
    if (!phone) {
      toast.error('Telephone abonne introuvable.');
      return;
    }
    if (!window.confirm(`Envoyer un OTP de reinitialisation du mot de passe app mobile au parent ${phone} ?`)) return;

    setResetLoading(parent.id);
    try {
      const { data } = await usersAPI.resetMobilePassword(phone);
      if (!data.ok) throw new Error(data.error || data.message || 'Reinitialisation impossible');
      const expiresAt = data.expiresAt ? new Date(data.expiresAt).toLocaleTimeString('fr-FR') : '5 minutes';
      toast.success(`OTP de reinitialisation envoye. Expiration: ${expiresAt}`);
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || error.message || 'Erreur reinitialisation');
    } finally {
      setResetLoading(null);
    }
  }

  const children = detail?.childrenList || [];
  const activeCount = parents.filter(p => p.status === 'active').length;
  const smsLowCount = parents.filter(p => Number(p.smsRemaining ?? 0) < 50).length;

  return (
    <div style={S.page}>
      <div style={S.layout}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <form style={S.topRow} onSubmit={submitSearch}>
            <input
              style={S.search}
              placeholder="Rechercher nom ou téléphone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button style={S.searchBtn} disabled={loading}>{loading ? 'Lecture...' : 'Rechercher'}</button>
            <button type="button" style={S.refreshBtn} onClick={() => loadParents(appliedSearch)} disabled={loading}>Rafraichir</button>
            <div style={S.counts}>
              <span style={{ color: '#22c55e' }}>{activeCount} actifs</span>
              <span style={{ color: '#f5a623', marginLeft: 12 }}>{smsLowCount} soldes SMS faibles</span>
              <span style={{ color: '#ffffff', marginLeft: 12 }}>{total} total DB</span>
            </div>
          </form>

          <div style={S.tableCard}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['PARENT', 'TÉLÉPHONE', 'PAYS', 'ENFANTS', 'SMS', 'PLAN', 'INSCRIT LE', 'STATUT'].map(h => <th key={h} style={S.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="8" style={S.empty}>Chargement des abonnés depuis PostgreSQL...</td></tr>}
                {!loading && filtered.map(p => (
                  <tr key={p.id} style={{ ...S.tr, background: detail?.id === p.id ? 'rgba(88,166,255,.09)' : 'transparent' }} onClick={() => setDetail(d => d?.id === p.id ? null : p)}>
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={p.name} />
                        <span style={{ fontSize: 12, color: '#ffffff', fontWeight: 700 }}>{p.name}</span>
                      </div>
                    </td>
                    <td style={{ ...S.td, fontFamily: 'monospace', color: '#ffffff' }}>{p.phone}</td>
                    <td style={S.td}><span title={p.country || 'n/a'}>{COUNTRY_FLAG[p.country] || p.country || 'n/a'}</span></td>
                    <td style={{ ...S.td, textAlign: 'center', color: '#f5a623', fontWeight: 800 }}>{p.children ?? 0}</td>
                    <td style={{ ...S.td, color: Number(p.smsRemaining ?? 0) < 50 ? '#f5a623' : '#22c55e', fontWeight: 800 }}>{p.smsRemaining ?? 0}</td>
                    <td style={S.td}><span style={{ ...S.tag, background: '#1e4db722', color: '#f5a623' }}>{p.plan || 'TOKKAM'}</span></td>
                    <td style={{ ...S.td, color: '#ffffff', fontSize: 11 }}>{formatDate(p.since)}</td>
                    <td style={S.td}><span style={{ ...S.tag, background: '#22c55e22', color: '#22c55e' }}>{p.status || 'active'}</span></td>
                  </tr>
                ))}
                {!loading && !filtered.length && <tr><td colSpan="8" style={S.empty}>Aucun abonné trouvé pour cette recherche.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {detail && (
          <div style={S.detail}>
            <div style={S.detailHeader}>
              <Avatar name={detail.name} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#ffffff' }}>{detail.name}</div>
                <div style={{ fontSize: 11, color: '#ffffff' }}>{detail.phone}</div>
              </div>
              <button style={S.closeBtn} onClick={() => setDetail(null)}>x</button>
            </div>

            {canResetPassword && (
              <div style={S.actionBox}>
                <button
                  style={S.resetBtn}
                  onClick={() => resetMobilePassword(detail)}
                  disabled={resetLoading === detail.id}
                >
                  {resetLoading === detail.id ? 'Envoi OTP...' : 'Reinitialiser mot de passe app'}
                </button>
                <div style={S.actionHint}>Envoie un OTP au parent pour choisir un nouveau mot de passe dans l'application mobile.</div>
              </div>
            )}

            <div style={S.detailSection}>INFOS COMPTE DB</div>
            {[
              ['Plan', detail.plan || 'TOKKAM'],
              ['Pays', detail.country || 'n/a'],
              ['Inscrit le', formatDate(detail.since)],
              ['Mis à jour', formatDate(detail.updatedAt)],
              ['Statut', detail.status || 'active'],
              ['Enfants principal', detail.principalChildren ?? 0],
              ['Co-parent', detail.coparentChildren ?? 0],
              ['Solde SMS', detail.smsRemaining ?? 0],
              ['Solde SMS maj', formatDate(detail.smsUpdatedAt)],
            ].map(([label, value]) => (
              <div key={label} style={S.field}>
                <span style={S.fieldLabel}>{label}</span>
                <span style={S.fieldVal}>{value}</span>
              </div>
            ))}

            <div style={S.detailSection}>ENFANTS ({children.length})</div>
            {children.map(c => (
              <div key={c.id} style={S.childRow}>
                <div style={S.childBall}>{String(c.name || '?')[0]}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#ffffff', fontWeight: 700 }}>{c.name}</div>
                  <div style={{ fontSize: 10, color: '#ffffff' }}>{c.relationRole} · {c.relationStatus} · batterie {c.batteryLevel ?? 'n/a'}%</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,.86)' }}>MAJ il y a {ageText(c.updatedAt)}</div>
                </div>
                <span style={{ ...S.tag, background: c.status === 'online' ? '#22c55e22' : '#e6394622', color: c.status === 'online' ? '#22c55e' : '#e63946', fontSize: 10 }}>{c.status}</span>
              </div>
            ))}
            {!children.length && <div style={S.emptySmall}>Aucun enfant associé dans la DB.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: { padding: 20 },
  layout: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  topRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  search: { background: '#10224a', border: '1px solid rgba(255,255,255,.18)', color: '#ffffff', fontFamily: 'monospace', fontSize: 12, padding: '9px 12px', borderRadius: 6, outline: 'none', width: 320 },
  searchBtn: { background: '#f5a623', border: 'none', color: '#0d1b3e', fontWeight: 900, padding: '9px 14px', borderRadius: 6, cursor: 'pointer' },
  refreshBtn: { background: '#1e4db7', border: 'none', color: '#ffffff', fontWeight: 800, padding: '9px 14px', borderRadius: 6, cursor: 'pointer' },
  counts: { fontSize: 12, marginLeft: 'auto' },
  tableCard: { background: '#10224a', border: '1px solid rgba(255,255,255,.16)', borderRadius: 6, overflow: 'auto' },
  th: { fontSize: 10, color: '#ffffff', textTransform: 'uppercase', letterSpacing: .8, padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.16)', textAlign: 'left', fontWeight: 800, whiteSpace: 'nowrap' },
  td: { color: '#ffffff', padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.10)', fontSize: 12, verticalAlign: 'middle' },
  tr: { cursor: 'pointer', transition: 'background .1s' },
  tag: { padding: '2px 7px', borderRadius: 3, fontSize: 11, fontWeight: 800 },
  empty: { color: '#ffffff', padding: 18, textAlign: 'center', fontSize: 13 },
  emptySmall: { color: '#ffffff', padding: 14, fontSize: 12 },
  detail: { width: 320, background: '#10224a', border: '1px solid rgba(255,255,255,.16)', borderRadius: 8, flexShrink: 0, overflow: 'hidden' },
  detailHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px', borderBottom: '1px solid rgba(255,255,255,.16)' },
  detailSection: { fontSize: 10, color: '#ffffff', fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase', padding: '12px 14px 5px', borderTop: '1px solid rgba(255,255,255,.10)' },
  field: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 14px', borderBottom: '1px solid rgba(255,255,255,.08)' },
  fieldLabel: { fontSize: 11, color: 'rgba(255,255,255,.86)' },
  fieldVal: { fontSize: 11, color: '#ffffff', textAlign: 'right', fontWeight: 700 },
  childRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid rgba(255,255,255,.08)' },
  childBall: { width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#ffffff', fontWeight: 800, flexShrink: 0 },
  actionBox: { padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,.10)', background: 'rgba(245,166,35,.06)' },
  resetBtn: { width: '100%', background: '#f5a623', border: 'none', color: '#0d1b3e', fontWeight: 900, padding: '9px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  actionHint: { marginTop: 7, color: 'rgba(255,255,255,.78)', fontSize: 10, lineHeight: 1.45 },
  closeBtn: { marginLeft: 'auto', background: 'none', border: 'none', color: '#ffffff', cursor: 'pointer', fontSize: 16 },
};
